(() => {
  const EQUIPMENT_QUERY_PREFIX = 'NOT product_type:Parts AND ';
  const PARTS_QUERY_PREFIX = 'product_type:Parts AND ';
  // Small, high-confidence corrections for common equipment-search wording.
  // Keep this list intentionally short so searches remain predictable.
  const SEARCH_CORRECTIONS = [
    [/\bhay\s+bosters?\b/gi, 'haybuster'],
    [/\bhay\s+busters?\b/gi, 'haybuster'],
    [/\bgrain\s+vaccs?\b/gi, 'grain vac'],
    [/\bgrain\s+vacuums?\b/gi, 'grain vac'],
    [/\bmulchr\b/gi, 'mulcher'],
    [/\btrailor\b/gi, 'trailer'],
    [/\btrailors\b/gi, 'trailers'],
    [/\bconveyer\b/gi, 'conveyor'],
    [/\bconveyers\b/gi, 'conveyors'],
    [/\bskidsteer\b/gi, 'skid steer']
  ];

  const normalizeSearchTerms = (value = '') => SEARCH_CORRECTIONS
    .reduce((query, [pattern, replacement]) => query.replace(pattern, replacement), value)
    .replace(/\s+/g, ' ')
    .trim();

  const unwrapPartNumberExpansion = (value = '') => {
    const match = value.match(/^\((.+)\s+OR\s+P(.+)\)$/i);
    if (!match || match[1].toLowerCase() !== match[2].toLowerCase()) return value;
    return match[1];
  };

  const cleanQuery = (value = '') => value
    .replace(/^NOT\s+product_type:Parts\s+AND\s+/i, '')
    .replace(/\s+AND\s+NOT\s+product_type:Parts\s*$/i, '')
    .replace(/^product_type:Parts\s+AND\s+/i, '')
    .trim();

  const cleanCustomerQuery = (value = '') => unwrapPartNumberExpansion(cleanQuery(value));

  const cleanDisplayText = (value = '') => value
    .replace(/NOT\s+product_type:Parts\s+AND\s+/gi, '')
    .replace(/\s+AND\s+NOT\s+product_type:Parts/gi, '')
    .replace(/product_type:Parts\s+AND\s+/gi, '')
    .replace(/\(([^()]+)\s+OR\s+P([^()]+)\)/gi, (match, number, prefixedNumber) => (
      number.toLowerCase() === prefixedNumber.toLowerCase() ? number : match
    ));

  const looksLikeUnprefixedPartNumber = (value = '') => (
    /^\d[\dA-Z./-]*(?:\s+\d[\dA-Z./-]*)*$/i.test(value)
  );

  const buildScopedSearchQuery = (value, scope) => {
    const query = normalizeSearchTerms(value);
    const searchTerms = scope === 'parts' && looksLikeUnprefixedPartNumber(query)
      ? `(${query} OR P${query})`
      : query;
    return `${scope === 'parts' ? PARTS_QUERY_PREFIX : EQUIPMENT_QUERY_PREFIX}${searchTerms}`;
  };

  const resolveInitialScope = (shell) => {
    const urlScope = new URLSearchParams(window.location.search).get('scope');
    if (urlScope === 'parts' || urlScope === 'equipment') return urlScope;
    return shell.dataset.defaultScope === 'parts' ? 'parts' : 'equipment';
  };

  const normalizeScopedSearchUrl = () => {
    if (window.location.pathname !== '/search') return true;

    const url = new URL(window.location.href);
    const scope = url.searchParams.get('scope');
    if (scope !== 'parts' && scope !== 'equipment') return true;

    const rawQuery = url.searchParams.get('q') || '';
    const query = cleanCustomerQuery(rawQuery);
    if (!query) return true;

    // Always remove the temporary product-type expression from the visible URL
    // after Shopify has used it to build the result set.
    if (rawQuery !== query) {
      url.searchParams.set('q', query);
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      return true;
    }

    if (!document.querySelector('[data-scope-query-fallback="true"]')) return true;

    url.searchParams.set('q', buildScopedSearchQuery(query, scope));
    window.location.replace(url.toString());
    return false;
  };

  const updateSearchResultsHeading = () => {
    if (window.location.pathname !== '/search') return;
    const heading = document.querySelector('[data-keast-search-results-heading]');
    if (!heading) return;

    const params = new URLSearchParams(window.location.search);
    const query = cleanCustomerQuery(params.get('q') || '');
    if (!query) return;

    const scope = params.get('scope')
      || document.querySelector('[data-keast-search-form][data-search-scope]')?.dataset.searchScope;
    const label = scope === 'parts' ? 'Parts' : scope === 'equipment' ? 'Equipment' : 'Search';
    heading.textContent = `${label} results for “${query}”`;
  };

  const updateShell = (shell, scope) => {
    const normalizedScope = scope === 'parts' ? 'parts' : 'equipment';
    const form = shell.querySelector('[data-keast-search-form]');
    const input = shell.querySelector('[data-keast-search-query]');
    if (!form || !input) return;

    form.dataset.searchScope = normalizedScope;
    input.placeholder = normalizedScope === 'parts'
      ? 'Part number, model or description'
      : 'Equipment, category or brand';

    const label = shell.querySelector('[data-keast-search-label]');
    if (label) {
      label.textContent = normalizedScope === 'parts' ? 'Search parts' : 'Search equipment';
    }

    const support = shell.closest('.keast-home-hero__actions')?.querySelector('[data-keast-search-support]');
    if (support) {
      support.textContent = normalizedScope === 'parts'
        ? support.dataset.partsText
        : support.dataset.equipmentText;
    }

    form.querySelectorAll('[data-keast-search-filter]').forEach((filterInput) => {
      filterInput.disabled = filterInput.dataset.keastSearchFilterScope !== normalizedScope;
    });

    const scopeInput = form.querySelector('[data-keast-search-scope-input]');
    if (scopeInput) scopeInput.value = normalizedScope;

    shell.querySelectorAll('[data-keast-search-scope]').forEach((control) => {
      const active = control.dataset.keastSearchScope === normalizedScope;
      if (control.matches('input[type="radio"]')) {
        control.checked = active;
      } else {
        control.classList.toggle('is-active', active);
        control.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-keast-search-scope]');
    if (!button) return;
    const shell = button.closest('[data-keast-search-shell]');
    if (!shell) return;
    updateShell(shell, button.dataset.keastSearchScope);
    shell.querySelector('[data-keast-search-query]')?.focus();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-keast-search-form]');
    if (!form) return;

    const input = form.querySelector('[data-keast-search-query]');
    const submitQuery = form.querySelector('[data-keast-search-submit-query]');
    if (!input || !submitQuery) return;

    const query = normalizeSearchTerms(cleanCustomerQuery(input.value));
    if (!query) {
      event.preventDefault();
      input.focus();
      return;
    }

    input.value = query;

    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId && !form.querySelector('input[name="preview_theme_id"]')) {
      const previewInput = document.createElement('input');
      previewInput.type = 'hidden';
      previewInput.name = 'preview_theme_id';
      previewInput.value = previewThemeId;
      form.appendChild(previewInput);
    }

    const useFallback = form.dataset.scopeQueryFallback === 'true';
    const scope = form.dataset.searchScope === 'parts' ? 'parts' : 'equipment';
    submitQuery.value = useFallback
      ? buildScopedSearchQuery(query, scope)
      : query;
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (!normalizeScopedSearchUrl()) return;
    document.title = cleanDisplayText(document.title);
    updateSearchResultsHeading();

    document.querySelectorAll('[data-keast-search-shell]').forEach((shell) => {
      const input = shell.querySelector('[data-keast-search-query]');
      if (input) input.value = cleanCustomerQuery(input.value);
      updateShell(shell, resolveInitialScope(shell));
    });
  });
})();
